import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  UploadCloud,
  FileText,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  Sparkles,
  Camera,
  Type,
  Plus,
  X,
  Calculator,
  Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Footer } from '../components/LegalDisclaimers';
import UserMenu from '../components/UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/listing-import`;

const NUMBER_FIELDS = new Set([
  'purchase_price',
  'num_units',
  'annual_income',
  'down_payment_percent',
  'interest_rate',
  'loan_term_years',
  'annual_property_tax',
  'annual_insurance',
  'monthly_maintenance',
  'utilities_electricity',
  'utilities_heating',
  'utilities_water',
  'utilities_sewer',
  'utilities_internet',
  'utilities_cable',
  'repairs_reserve_annual',
  'snow_removal_annual',
  'landscaping_annual',
  'cleaning_common_annual',
  'security_alarm_annual',
  'garbage_waste_annual',
  'vacancy_rate',
  'property_management_percent',
]);

const FIELD_GROUPS = [
  {
    key: 'property',
    titleKey: 'listingImport.groups.property',
    fields: ['property_address', 'purchase_price', 'num_units', 'annual_income'],
  },
  {
    key: 'financing',
    titleKey: 'listingImport.groups.financing',
    fields: ['down_payment_percent', 'interest_rate', 'loan_term_years'],
  },
  {
    key: 'opex',
    titleKey: 'listingImport.groups.opex',
    fields: ['annual_property_tax', 'annual_insurance', 'monthly_maintenance'],
  },
  {
    key: 'utilities',
    titleKey: 'listingImport.groups.utilities',
    fields: [
      'utilities_electricity',
      'utilities_heating',
      'utilities_water',
      'utilities_sewer',
      'utilities_internet',
      'utilities_cable',
    ],
  },
  {
    key: 'additional',
    titleKey: 'listingImport.groups.additional',
    fields: [
      'repairs_reserve_annual',
      'snow_removal_annual',
      'landscaping_annual',
      'cleaning_common_annual',
      'security_alarm_annual',
      'garbage_waste_annual',
    ],
  },
  {
    key: 'assumptions',
    titleKey: 'listingImport.groups.assumptions',
    fields: ['vacancy_rate', 'property_management_percent'],
  },
];

function StatusBadge({ status, t }) {
  const map = {
    extracted: {
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900',
    },
    calculated: {
      icon: <Calculator className="w-3.5 h-3.5" />,
      cls: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-900',
    },
    missing: {
      icon: <Info className="w-3.5 h-3.5" />,
      cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700',
    },
    conflicting: {
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      cls: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800',
    },
    user_confirmed: {
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900',
    },
    user_modified: {
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900',
    },
  };
  const entry = map[status] || map.missing;
  return (
    <span
      data-testid={`field-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${entry.cls}`}
    >
      {entry.icon} {t(`listingImport.status.${status}`)}
    </span>
  );
}

function FieldRow({ field, t, onChange, sourceLabelById }) {
  const isNumber = NUMBER_FIELDS.has(field.field);
  const observations = field.observations || [];
  const [pickerOpen, setPickerOpen] = useState(false);

  const source =
    field.source_id && sourceLabelById[field.source_id]
      ? sourceLabelById[field.source_id]
      : null;

  const inputValue = field.value === null || field.value === undefined ? '' : field.value;

  const handleValueChange = (v) => {
    if (isNumber && v !== '') {
      const parsed = parseFloat(v);
      if (Number.isNaN(parsed)) return;
      onChange(field.field, { value: parsed, status: 'user_modified' });
    } else {
      onChange(field.field, { value: v, status: v ? 'user_modified' : 'missing' });
    }
  };

  return (
    <div
      data-testid={`field-row-${field.field}`}
      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t(`listingImport.fields.${field.field}`)}
          </Label>
        </div>
        <StatusBadge status={field.status} t={t} />
      </div>

      {field.status === 'conflicting' && observations.length > 1 ? (
        <div className="space-y-2">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {t('listingImport.review.conflictHelp')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {observations.map((o, idx) => {
              const label = sourceLabelById[o.source_id] || o.source_id;
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`conflict-choice-${field.field}-${idx}`}
                  onClick={() =>
                    onChange(field.field, {
                      value: o.value,
                      status: 'user_confirmed',
                    })
                  }
                  className="text-left rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20 p-2 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                >
                  <div className="text-[10px] uppercase text-amber-700 dark:text-amber-300 font-semibold">
                    {label}
                  </div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {typeof o.value === 'number'
                      ? o.value.toLocaleString()
                      : String(o.value)}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Input
              data-testid={`field-input-${field.field}`}
              type={isNumber ? 'number' : 'text'}
              value={inputValue}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={t('listingImport.review.orEnterCustom')}
              className="rounded-lg"
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            data-testid={`field-input-${field.field}`}
            type={isNumber ? 'number' : 'text'}
            value={inputValue}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={t('listingImport.review.notFound')}
            className="rounded-lg"
          />
        </div>
      )}

      {(source || field.original_text) && (
        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 truncate">
          {source && (
            <span className="font-semibold">
              {t('listingImport.review.source')}: {source}
              {field.source_page ? ` · p.${field.source_page}` : ''}
            </span>
          )}
          {source && field.original_text && ' — '}
          {field.original_text && <em>"{field.original_text}"</em>}
        </p>
      )}
    </div>
  );
}

export default function ListingImport() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null); // {session_id, status, sources, fields, additional_expenses}
  const [tab, setTab] = useState('upload'); // 'upload' | 'text'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Bootstrap: fetch user + create session.
  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true,
        });
        setUser(me.data);
      } catch {
        navigate('/');
        return;
      }
      try {
        const resp = await axios.post(`${API}/session`, {}, { withCredentials: true });
        setSession({
          session_id: resp.data.session_id,
          status: 'draft',
          sources: [],
          fields: [],
          additional_expenses: [],
          warnings: [],
        });
      } catch {
        toast.error(t('listingImport.errors.sessionCreate'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceLabelById = useMemo(() => {
    const map = {};
    (session?.sources || []).forEach((s) => {
      map[s.source_id] = s.label || s.filename || s.source_id;
    });
    return map;
  }, [session]);

  const uploadFiles = useCallback(
    async (files) => {
      if (!session) return;
      const arr = Array.from(files || []).filter(Boolean);
      if (!arr.length) return;
      setBusy(true);
      let latest = session;
      for (const f of arr) {
        try {
          const form = new FormData();
          form.append('file', f);
          const resp = await axios.post(
            `${API}/session/${session.session_id}/sources/file`,
            form,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
          );
          latest = resp.data;
          setSession(latest);
        } catch (err) {
          const msg = err.response?.data?.detail || t('listingImport.errors.uploadFailed');
          toast.error(typeof msg === 'string' ? msg : t('listingImport.errors.uploadFailed'));
        }
      }
      setBusy(false);
    },
    [session, t],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      const files = e.dataTransfer?.files;
      if (files && files.length) uploadFiles(files);
    },
    [uploadFiles],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragActive(false), []);

  // Clipboard paste of images.
  useEffect(() => {
    const onPaste = (e) => {
      if (!session) return;
      // Ignore paste while typing inside inputs/textarea/contenteditable.
      const tgt = e.target;
      const tag = tgt?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt?.isContentEditable) return;
      const items = e.clipboardData?.items || [];
      const files = [];
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        e.preventDefault();
        uploadFiles(files);
        toast.success(t('listingImport.toast.pastedScreenshot'));
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [session, uploadFiles, t]);

  const submitText = async () => {
    if (!session || !text.trim()) return;
    setBusy(true);
    try {
      const resp = await axios.post(
        `${API}/session/${session.session_id}/sources/text`,
        { text: text.trim() },
        { withCredentials: true },
      );
      setSession(resp.data);
      setText('');
      toast.success(t('listingImport.toast.textAdded'));
    } catch (err) {
      const msg = err.response?.data?.detail || t('listingImport.errors.uploadFailed');
      toast.error(typeof msg === 'string' ? msg : t('listingImport.errors.uploadFailed'));
    }
    setBusy(false);
  };

  const removeSource = async (source_id) => {
    if (!session) return;
    try {
      const resp = await axios.delete(
        `${API}/session/${session.session_id}/sources/${source_id}`,
        { withCredentials: true },
      );
      setSession(resp.data);
    } catch {
      toast.error(t('listingImport.errors.removeFailed'));
    }
  };

  const runExtraction = async () => {
    if (!session || !(session.sources || []).length) return;
    setExtracting(true);
    try {
      const resp = await axios.post(
        `${API}/session/${session.session_id}/extract`,
        {},
        { withCredentials: true },
      );
      setSession(resp.data);
      toast.success(t('listingImport.toast.extracted'));
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) {
        toast.error(t('listingImport.errors.extractionUnavailable'));
      } else {
        toast.error(t('listingImport.errors.extractionFailed'));
      }
    }
    setExtracting(false);
  };

  const patchField = async (field, patch) => {
    if (!session) return;
    // Optimistic UI: update locally, then sync.
    setSession((prev) => {
      if (!prev) return prev;
      const fields = prev.fields.map((f) =>
        f.field === field
          ? {
              ...f,
              value: patch.value === undefined ? f.value : patch.value,
              status: patch.status || f.status,
            }
          : f,
      );
      // If field doesn't exist yet, append it.
      if (!fields.find((f) => f.field === field)) {
        fields.push({
          field,
          value: patch.value ?? null,
          status: patch.status || 'user_modified',
          observations: [],
        });
      }
      return { ...prev, fields };
    });
    try {
      await axios.patch(
        `${API}/session/${session.session_id}/fields`,
        { updates: { [field]: patch } },
        { withCredentials: true },
      );
    } catch {
      // Silent — the next extract or confirm will re-sync.
    }
  };

  const updateAdditional = async (list) => {
    setSession((prev) => (prev ? { ...prev, additional_expenses: list } : prev));
    try {
      await axios.patch(
        `${API}/session/${session.session_id}/fields`,
        { updates: { additional_operating_expenses: list } },
        { withCredentials: true },
      );
    } catch {
      /* silent */
    }
  };

  const confirmAndUse = async () => {
    if (!session) return;
    // Snapshot the CURRENT Analyzer defaults so back-end can preserve
    // any financing/assumption values that the source didn't specify.
    const analyzer_defaults = {
      down_payment_percent: 20,
      interest_rate: 7.0,
      loan_term_years: 30,
      vacancy_rate: 5,
      property_management_percent: 10,
    };
    try {
      const resp = await axios.post(
        `${API}/session/${session.session_id}/confirm`,
        { analyzer_defaults },
        { withCredentials: true },
      );
      // Stash payload in sessionStorage for /analyze to pick up.
      try {
        window.sessionStorage.setItem(
          'rendplex.listing_import_prefill',
          JSON.stringify(resp.data.prefill || {}),
        );
      } catch {
        /* ignore quota errors */
      }
      toast.success(t('listingImport.toast.usedValues'));
      navigate('/analyze?source=listing_import');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.error === 'unresolved_conflicts') {
        toast.error(t('listingImport.errors.unresolvedConflicts'));
      } else {
        toast.error(t('listingImport.errors.confirmFailed'));
      }
    }
  };

  const detectedCount = useMemo(
    () =>
      (session?.fields || []).filter((f) =>
        ['extracted', 'calculated', 'user_confirmed', 'user_modified'].includes(f.status),
      ).length,
    [session],
  );
  const conflictCount = useMemo(
    () => (session?.fields || []).filter((f) => f.status === 'conflicting').length,
    [session],
  );

  const hasReview = session?.status === 'extracted' || session?.status === 'confirmed';

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground">
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2">
            <RendPlexLogo size="md" />
          </Link>
          <div className="flex items-center gap-2">
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
            <Button
              data-testid="li-back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/analyze')}
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('nav.backToDashboard')}
            </Button>
            <UserMenu user={user} />
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-[#45C04A]/10 text-[#45C04A] text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> {t('listingImport.badge')}
          </div>
          <h1
            data-testid="listing-import-title"
            className="font-primary font-extrabold text-3xl lg:text-4xl text-[#12355B] dark:text-white"
          >
            {t('listingImport.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-2xl">
            {t('listingImport.subtitle')}
          </p>
        </div>

        {/* Upload / paste text */}
        {!hasReview && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-2">
                <Button
                  data-testid="li-tab-upload"
                  variant={tab === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTab('upload')}
                  className="rounded-full"
                >
                  <UploadCloud className="w-4 h-4 mr-1.5" />
                  {t('listingImport.tabs.upload')}
                </Button>
                <Button
                  data-testid="li-tab-text"
                  variant={tab === 'text' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTab('text')}
                  className="rounded-full"
                >
                  <Type className="w-4 h-4 mr-1.5" />
                  {t('listingImport.tabs.text')}
                </Button>
              </div>

              {tab === 'upload' ? (
                <div
                  ref={dropRef}
                  data-testid="li-dropzone"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`rounded-2xl border-2 border-dashed p-8 md:p-12 text-center transition-colors ${
                    dragActive
                      ? 'border-[#45C04A] bg-[#45C04A]/5'
                      : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-[#131824]'
                  }`}
                >
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-[#45C04A]/10 flex items-center justify-center mb-4">
                    <UploadCloud className="w-7 h-7 text-[#45C04A]" />
                  </div>
                  <p className="text-lg font-semibold text-[#12355B] dark:text-white">
                    {t('listingImport.dropzone.title')}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {t('listingImport.dropzone.hint')}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    <Button
                      data-testid="li-choose-files-btn"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white"
                    >
                      <UploadCloud className="w-4 h-4 mr-1.5" />
                      {t('listingImport.dropzone.chooseFiles')}
                    </Button>
                    <Button
                      data-testid="li-take-photo-btn"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-xl md:hidden"
                    >
                      <Camera className="w-4 h-4 mr-1.5" />
                      {t('listingImport.dropzone.takePhoto')}
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.webp,.pdf,image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => uploadFiles(e.target.files)}
                    data-testid="li-file-input"
                  />
                  <p className="mt-4 text-xs text-slate-400">
                    {t('listingImport.dropzone.limits')}
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 p-4 md:p-6">
                  <Label htmlFor="li-text-input" className="mb-2 block">
                    {t('listingImport.text.label')}
                  </Label>
                  <Textarea
                    id="li-text-input"
                    data-testid="li-text-input"
                    rows={10}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('listingImport.text.placeholder')}
                    className="rounded-xl"
                  />
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <Button
                      data-testid="li-text-submit"
                      onClick={submitText}
                      disabled={!text.trim() || busy}
                      className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 text-white"
                    >
                      <Copy className="w-4 h-4 mr-1.5" />
                      {t('listingImport.text.submit')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-3 flex gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span data-testid="li-legal-ack">{t('listingImport.legal.ack')}</span>
              </div>
            </div>

            {/* Sources list */}
            <aside className="rounded-2xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 p-4 md:p-6">
              <h3 className="font-semibold text-[#12355B] dark:text-white text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('listingImport.sources.title', { count: session?.sources?.length || 0 })}
              </h3>
              <div className="mt-3 space-y-2" data-testid="li-source-list">
                {(session?.sources || []).length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('listingImport.sources.empty')}
                  </p>
                ) : (
                  session.sources.map((s) => (
                    <div
                      key={s.source_id}
                      data-testid={`li-source-${s.source_id}`}
                      className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#12355B] dark:text-white truncate">
                          {s.label || s.filename || s.source_id}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {s.kind === 'text'
                            ? t('listingImport.sources.pastedText')
                            : s.filename || s.mime_type}
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={`li-source-remove-${s.source_id}`}
                        onClick={() => removeSource(s.source_id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        aria-label={t('listingImport.sources.remove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <Button
                data-testid="li-extract-btn"
                disabled={!session?.sources?.length || extracting}
                onClick={runExtraction}
                className="mt-4 w-full rounded-xl bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] font-semibold"
              >
                {extracting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    {t('listingImport.processing.reading')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    {t('listingImport.extractBtn')}
                  </>
                )}
              </Button>
            </aside>
          </div>
        )}

        {/* Review */}
        {hasReview && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 p-4 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('listingImport.review.summary', {
                    detected: detectedCount,
                    conflicts: conflictCount,
                  })}
                </p>
                {(session.warnings || []).map((w, idx) => (
                  <p key={idx} className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    ⚠ {w}
                  </p>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  data-testid="li-add-more-btn"
                  onClick={() =>
                    setSession((prev) => (prev ? { ...prev, status: 'draft' } : prev))
                  }
                  className="rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  {t('listingImport.review.addMore')}
                </Button>
                <Button
                  data-testid="li-use-values-btn"
                  onClick={confirmAndUse}
                  className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white font-semibold"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  {t('listingImport.review.useValues')}
                </Button>
              </div>
            </div>

            {FIELD_GROUPS.map((group) => (
              <div
                key={group.key}
                data-testid={`li-group-${group.key}`}
                className="rounded-2xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 p-4 md:p-6"
              >
                <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-4">
                  {t(group.titleKey)}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {group.fields.map((f) => {
                    const field =
                      (session.fields || []).find((x) => x.field === f) || {
                        field: f,
                        value: null,
                        status: 'missing',
                        observations: [],
                      };
                    return (
                      <FieldRow
                        key={f}
                        field={field}
                        t={t}
                        onChange={patchField}
                        sourceLabelById={sourceLabelById}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Additional Other Operating Expenses */}
            <div
              data-testid="li-group-other-expenses"
              className="rounded-2xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 p-4 md:p-6"
            >
              <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-4">
                {t('listingImport.groups.otherExpenses')}
              </h2>
              <div className="space-y-2">
                {(session.additional_expenses || []).map((row, idx) => (
                  <div
                    key={idx}
                    data-testid={`li-other-expense-${idx}`}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 px-3 py-2"
                  >
                    <Input
                      type="text"
                      value={row.name}
                      onChange={(e) => {
                        const next = [...session.additional_expenses];
                        next[idx] = { ...row, name: e.target.value };
                        updateAdditional(next);
                      }}
                      placeholder={t('listingImport.groups.expenseName')}
                      className="flex-1 rounded-lg"
                    />
                    <Input
                      type="number"
                      value={row.annual_amount}
                      onChange={(e) => {
                        const next = [...session.additional_expenses];
                        next[idx] = {
                          ...row,
                          annual_amount: parseFloat(e.target.value) || 0,
                        };
                        updateAdditional(next);
                      }}
                      placeholder={t('listingImport.groups.expenseAmount')}
                      className="w-32 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        updateAdditional(session.additional_expenses.filter((_, i) => i !== idx))
                      }
                      className="text-slate-400 hover:text-red-500"
                      aria-label={t('listingImport.sources.remove')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="li-add-expense-btn"
                  onClick={() =>
                    updateAdditional([
                      ...(session.additional_expenses || []),
                      { name: '', annual_amount: 0, status: 'user_modified' },
                    ])
                  }
                  className="rounded-lg"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> {t('listingImport.groups.addExpense')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
